import { useId, useState, useRef, useMemo, FunctionComponent } from 'react';
import invariant from 'invariant';
import useEventCallback from '@mui/utils/useEventCallback';
import { DialogsContext } from './DialogsContext';
import type { DialogComponent, OpenDialog, OpenDialogOptions } from '../Dialog.interface';
import { IDialogStackEntry, IDialogsProviderProps } from './DialogsProvider.interface';

/**
 * Provider for Dialog stacks. The subtree of this component can use the `useDialogs` hook to
 * access the dialogs API. The dialogs are rendered in the order they are requested.
 *
 * Demos:
 *
 * - [useDialogs](https://mui.com/toolpad/core/react-use-dialogs/)
 *
 * API:
 *
 * - [DialogsProvider API](https://mui.com/toolpad/core/api/dialogs-provider)
 */
export const DialogsProvider: FunctionComponent<IDialogsProviderProps> = (props) => {
  const { children, unmountAfter = 1000 } = props;
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const [stack, setStack] = useState<IDialogStackEntry<any, any>[]>([]);
  const keyPrefix = useId();
  const nextId = useRef(0);

  const requestDialog = useEventCallback<OpenDialog>(function open<P, R>(
    Component: DialogComponent<P, R>,
    payload: P,
    options: OpenDialogOptions<R> = {},
  ) {
    const { onClose = async () => {/* noop */} } = options;
    let resolve: ((result: R) => void) | undefined;
    const promise = new Promise<R>((resolveImpl) => {
      resolve = resolveImpl;
    });
    invariant(resolve, 'resolve not set');

    const key = `${keyPrefix}-${nextId.current}`;
    nextId.current += 1;

    const newEntry: IDialogStackEntry<P, R> = {
      key,
      open: true,
      promise,
      Component,
      payload,
      onClose,
      resolve,
    };

    setStack((prevStack) => [...prevStack, newEntry]);
    return promise;
  });

  const closeDialogUi = useEventCallback(function closeDialogUi<R>(dialog: Promise<R>) {
    setStack((prevStack) =>
      prevStack.map((entry) => (entry.promise === dialog ? { ...entry, open: false } : entry)),
    );
    setTimeout(() => {
      // wait for closing animation
      setStack((prevStack) => prevStack.filter((entry) => entry.promise !== dialog));
    }, unmountAfter);
  });

  const closeDialog = useEventCallback(async function closeDialog<R>(
    dialog: Promise<R>,
    result: R,
  ) {
    const entryToClose = stack.find((entry) => entry.promise === dialog);
    invariant(entryToClose, 'dialog not found');
    await entryToClose.onClose(result);
    entryToClose.resolve(result);
    closeDialogUi(dialog);
    return dialog;
  });

  const contextValue = useMemo(
    () => ({ open: requestDialog, close: closeDialog }),
    [requestDialog, closeDialog],
  );

  return (
    <DialogsContext.Provider value={contextValue}>
      {children}
      {stack.map(({ key, open, Component, payload, promise }) => (
        <Component
          key={key}
          payload={payload}
          open={open}
          onClose={async (result) => {
            await closeDialog(promise, result);
          }}
        />
      ))}
    </DialogsContext.Provider>
  );
};
